import React, { CSSProperties } from 'react';
import { addMonths } from 'date-fns';
import { useDateNow, useReduxState } from 'hooks';
import {
  CardProgressStatus,
  CourseProgressTooltip
} from 'components/common/tasks';

import type { CourseProgress } from 'shared-types';
import type { BadgeSize } from 'components/common/Badge';

interface Props {
  completionEnabled: boolean;
  id: number | string;
  progressData: CourseProgress;
  showNotStarted?: boolean;
  showProgressTextLabel?: boolean;
  size?: BadgeSize;
  style?: CSSProperties;
}

const CoursesTileProgress = ({
  completionEnabled,
  id,
  progressData,
  style = {}
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, serverConfig } = useReduxState();

  const {
    completion,
    numberOfTasks,
    numberOfVideos,
    readingProgress,
    taskAnswers,
    taskMaxPoints,
    taskPoints,
    taskPointsProgress,
    videoProgress,
    videoProgressVideosWatched
  } = progressData;

  let totalProgress = readingProgress;
  let numberOfProgresses = 1;

  if (numberOfTasks && taskAnswers !== undefined) {
    totalProgress += taskAnswers / numberOfTasks;
    numberOfProgresses++;
  }

  if (serverConfig.videoRequirementsType === 'individual') {
    if (numberOfVideos && videoProgressVideosWatched !== undefined) {
      totalProgress += videoProgressVideosWatched / numberOfVideos;
      numberOfProgresses++;
    }
  } else {
    if (numberOfVideos && videoProgress !== undefined) {
      totalProgress += videoProgress;
      numberOfProgresses++;
    }
  }

  if (numberOfProgresses) {
    totalProgress = totalProgress / numberOfProgresses;
  }

  const expired =
    completion?.expirationDate &&
    Date.parse(completion.expirationDate as string) < dateNow;
  const expiring =
    !expired &&
    completion?.expirationDate &&
    new Date(Date.parse(completion.expirationDate as string)) <
      addMonths(dateNow, 3);

  return (
    <div className="status" style={{ ...style }}>
      {completionEnabled ? (
        <CourseProgressTooltip
          attribute={`data-course-progress-status-${id}`}
          discrepancyInCompletion={progressData?.discrepancyInCompletion}
          numberOfTasks={numberOfTasks}
          numberOfVideos={numberOfVideos}
          readingProgress={readingProgress}
          showAnswers={true}
          showReading={true}
          showTaskPoints={true}
          showVideos={true}
          taskAnswers={taskAnswers}
          taskMaxPoints={taskMaxPoints}
          taskPoints={taskPoints}
          taskPointsProgress={taskPointsProgress}
          videoProgress={
            serverConfig.videoRequirementsType === 'individual'
              ? videoProgressVideosWatched / numberOfVideos
              : videoProgress
          }
          videosWatched={videoProgressVideosWatched}
        />
      ) : null}

      {completion &&
      !completion?.inactive &&
      completion?.status !== 'inprogress' ? (
        <div className="completion">
          <CardProgressStatus
            category={
              expired
                ? 'danger'
                : expiring
                ? 'warning'
                : completion.status === 'success'
                ? 'success'
                : completion.status === 'failed'
                ? 'danger'
                : 'default'
            }
            icon={
              completion.status === 'failed'
                ? 'times'
                : completion.status === 'waiting-for-reviews'
                ? 'hourglass-start'
                : 'check'
            }
            label={
              completion.status === 'failed'
                ? messages.readerCompletionFailed
                : completion.status === 'waiting-for-reviews'
                ? messages.completionsWaitingForReviews
                : expired
                ? messages.completionsExpired
                : expiring
                ? messages.completionsExpiring
                : messages.commonCompleted
            }
          />
        </div>
      ) : (
        <div
          className="progress-wrapper"
          {...{ [`data-course-progress-status-${id}`]: true }}
        >
          <CardProgressStatus
            label={
              !completionEnabled
                ? messages.coursesHasBeenRead
                : totalProgress
                ? messages.commonInProgress
                : messages.commonNotStarted
            }
            progress={completionEnabled ? totalProgress : readingProgress}
            progressShowIfZero={true}
            progressValueLabel={`${Math.round(
              (completionEnabled ? totalProgress : readingProgress) * 100
            )}%`}
          />
        </div>
      )}
    </div>
  );
};

export default CoursesTileProgress;
